<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import Stella from "../dashboard/avatars/stella.vue";
import { componentCatalog } from './component-catalog';

const props = defineProps({
    messages: { type: Array, default: () => [] },
    isGenerating: { type: Boolean, default: false },
    pendingAttachments: { type: Array, default: () => [] },
});

const emit = defineEmits(['send', 'configure-attachment', 'remove-attachment']);

const messageInput = ref('');
const messagesContainer = ref(null);
const isComponentPickerOpen = ref(false);

const canSend = computed(() =>
    !props.isGenerating && (messageInput.value.trim() !== '' || props.pendingAttachments.length > 0)
);

const componentLabel = (type) => {
    const component = componentCatalog.find((entry) => entry.type === type);

    return component ? _mft(component.nameKey) : type;
};

const attachmentLabel = (attachment) => attachment?.label || componentLabel(attachment?.type);

const toggleComponentPicker = () => {
    if (props.isGenerating) return;
    isComponentPickerOpen.value = !isComponentPickerOpen.value;
};

const attachComponent = (component) => {
    isComponentPickerOpen.value = false;
    emit('configure-attachment', { type: component.type, config: {}, index: null });
};

const removeAttachment = (index) => {
    emit('remove-attachment', index);
};

const editAttachment = (attachment, index) => {
    emit('configure-attachment', {
        type: attachment.type,
        config: attachment.config ?? {},
        index,
    });
};
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);
const assistantStatusMessageKeys = ref([]);

const pageUpdatedMessageKeys = [
    'page:aiPageBuilder.pageUpdatedMessages.1',
    'page:aiPageBuilder.pageUpdatedMessages.2',
    'page:aiPageBuilder.pageUpdatedMessages.3',
    'page:aiPageBuilder.pageUpdatedMessages.4',
    'page:aiPageBuilder.pageUpdatedMessages.5',
    'page:aiPageBuilder.pageUpdatedMessages.6',
    'page:aiPageBuilder.pageUpdatedMessages.7',
    'page:aiPageBuilder.pageUpdatedMessages.8',
    'page:aiPageBuilder.pageUpdatedMessages.9',
    'page:aiPageBuilder.pageUpdatedMessages.10',
];

// Each icon is a Heroicon outline SVG path (24x24 viewBox)
const funMessages = [
    { icon: 'M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18', textKey: 'page:aiPageBuilder.funMessages.1' },
    { icon: 'M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z', textKey: 'page:aiPageBuilder.funMessages.2' },
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z', textKey: 'page:aiPageBuilder.funMessages.3' },
    { icon: 'M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.047 8.287 8.287 0 009 9.601a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z M12 18a3.75 3.75 0 00.495-7.468 5.99 5.99 0 00-1.925 3.547 5.975 5.975 0 01-2.133-1.001A3.75 3.75 0 0012 18z', textKey: 'page:aiPageBuilder.funMessages.4' },
    { icon: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z', textKey: 'page:aiPageBuilder.funMessages.5' },
    { icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z', textKey: 'page:aiPageBuilder.funMessages.6' },
    { icon: 'M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.023 6.023 0 01-2.77.704 6.023 6.023 0 01-2.77-.704', textKey: 'page:aiPageBuilder.funMessages.7' },
    { icon: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z', textKey: 'page:aiPageBuilder.funMessages.8' },
    { icon: 'M6 13.5V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m12-3V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m-6-9V3.75m0 3.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 9.75V10.5', textKey: 'page:aiPageBuilder.funMessages.9' },
    { icon: 'M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z', textKey: 'page:aiPageBuilder.funMessages.10' },
    { icon: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418', textKey: 'page:aiPageBuilder.funMessages.11' },
    { icon: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.58-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z', textKey: 'page:aiPageBuilder.funMessages.12' },
    { icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z', textKey: 'page:aiPageBuilder.funMessages.13' },
    { icon: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6', textKey: 'page:aiPageBuilder.funMessages.14' },
    { icon: 'M11.42 15.17l-5.1-5.1a.5.5 0 010-.708l.707-.707a.5.5 0 01.708 0L11.42 12.34l5.66-5.66a.5.5 0 01.707 0l.708.707a.5.5 0 010 .708l-6.368 6.368a.5.5 0 01-.707 0z M21 12a9 9 0 11-18 0 9 9 0 0118 0z', textKey: 'page:aiPageBuilder.funMessages.15' },
];

const currentFunMessage = computed(() => {
    const message = funMessages[funMessageIndex.value % funMessages.length];

    return {
        ...message,
        text: _mft(message.textKey),
    };
});

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    // Smooth fake progress that slows down as it approaches 90%
    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

const getRandomPageUpdatedMessageKey = (previousKey = null) => {
    const availableKeys = pageUpdatedMessageKeys.filter((key) => key !== previousKey);
    const candidateKeys = availableKeys.length > 0 ? availableKeys : pageUpdatedMessageKeys;
    const randomIndex = Math.floor(Math.random() * candidateKeys.length);

    return candidateKeys[randomIndex];
};

const syncAssistantStatusMessages = () => {
    const nextKeys = [];
    let previousAssistantKey = null;

    props.messages.forEach((message, index) => {
        if (message.role !== 'assistant') {
            nextKeys[index] = null;
            return;
        }

        const existingKey = assistantStatusMessageKeys.value[index];
        if (existingKey) {
            nextKeys[index] = existingKey;
            previousAssistantKey = existingKey;
            return;
        }

        const nextKey = getRandomPageUpdatedMessageKey(previousAssistantKey);
        nextKeys[index] = nextKey;
        previousAssistantKey = nextKey;
    });

    assistantStatusMessageKeys.value = nextKeys;
};

const getAssistantStatusMessageKey = (index) => {
    return assistantStatusMessageKeys.value[index] || pageUpdatedMessageKeys[0];
};

const getAssistantMessageText = (message, index) => {
    if (typeof message?.summary === 'string' && message.summary.trim() !== '') {
        return message.summary.trim();
    }

    return _mft(getAssistantStatusMessageKey(index));
};

watch(() => props.isGenerating, (val) => {
    if (val) {
        startFunMessages();
    } else {
        stopFunMessages();
    }
});

watch(() => props.messages, () => {
    syncAssistantStatusMessages();
}, { deep: true, immediate: true });

const sendMessage = () => {
    if (!canSend.value) return;
    const text = messageInput.value.trim();
    messageInput.value = '';
    isComponentPickerOpen.value = false;
    emit('send', text);
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

watch(() => props.messages.length, () => {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
});

onUnmounted(() => {
    stopFunMessages();
});
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Chat header -->
        <div class="px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
            <stella class="w-6 h-6" />
            <h3 class="text-sm font-semibold text-gray-700"><mf-translate domainkey="page:aiPageBuilder.title"></mf-translate></h3>
        </div>

        <!-- Messages -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
            <div v-if="messages.length === 0 && !isGenerating" class="text-center text-gray-400 text-sm mt-8">
                <p class="mb-2"><mf-translate domainkey="page:aiPageBuilder.emptyState"></mf-translate></p>
                <p class="text-xs"><mf-translate domainkey="page:aiPageBuilder.emptyStateExample"></mf-translate></p>
            </div>

            <div
                v-for="(msg, index) in messages"
                :key="index"
                :class="msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'"
            >
                <div
                    :class="msg.role === 'user'
                        ? 'bg-aiAgentWebDesigner text-gray-900 rounded-2xl rounded-br-md px-4 py-2 max-w-[85%]'
                        : 'bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-2 max-w-[85%]'"
                    class="text-sm whitespace-pre-line"
                >
                    <template v-if="msg.role === 'user'">
                        <span v-if="msg.content">{{ msg.content }}</span>
                        <div
                            v-if="Array.isArray(msg.attachments) && msg.attachments.length"
                            class="flex flex-wrap gap-1"
                            :class="{ 'mt-2': msg.content }"
                        >
                            <span
                                v-for="(attachment, attachmentIndex) in msg.attachments"
                                :key="attachmentIndex"
                                class="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"/>
                                </svg>
                                {{ attachmentLabel(attachment) }}
                            </span>
                        </div>
                    </template>
                    <template v-else-if="msg.notice">
                        {{ msg.notice }}
                        <utility-ai-usage-cta v-if="msg.budgetExceeded" class="mt-2 whitespace-normal" />
                    </template>
                    <template v-else>
                        <span
                            class="text-xs"
                            :class="typeof msg.summary === 'string' && msg.summary.trim() !== '' ? 'text-gray-700' : 'text-gray-500'"
                        >{{ getAssistantMessageText(msg, index) }}</span>
                    </template>
                </div>
            </div>

            <!-- Generating: fancy animated card -->
            <div v-if="isGenerating" class="flex justify-start generating-appear">
                <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-aiAgentWebDesigner bg-gradient-to-br from-aiAgentWebDesigner/80 via-white to-aiAgentWebDesigner/30">
                    <!-- Progress bar -->
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div
                            class="h-full bg-aiAgentWebDesigner transition-all duration-700 ease-out"
                            :style="{ width: progressWidth + '%' }"
                        ></div>
                    </div>

                    <div class="px-5 py-4">
                        <!-- Icon + message -->
                        <div class="flex items-center gap-3 mb-3">
                            <div class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-aiAgentWebDesigner flex items-center justify-center" :key="funMessageIndex">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text }}</span>
                        </div>

                        <!-- Skeleton shimmer lines -->
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentWebDesigner/60 skeleton-shimmer" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-aiAgentWebDesigner/40 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentWebDesigner/40 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                <div class="h-2 rounded-full bg-aiAgentWebDesigner/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-aiAgentWebDesigner/60 skeleton-shimmer" style="width: 55%; animation-delay: 0.6s"></div>
                                <div class="h-2 rounded-full bg-aiAgentWebDesigner/40 skeleton-shimmer" style="width: 20%; animation-delay: 0.75s"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input area -->
        <div class="relative border-t border-gray-200 p-3 pb-2">
            <!-- Component picker popover -->
            <div
                class="transition-all"
                :class="isComponentPickerOpen ? 'max-h-96 mb-4' : 'max-h-0'"
            >
                <div
                    class="-mx-3 transition-all duration-500 relative"
                    :class="isComponentPickerOpen ? 'opacity-100' : 'opacity-0'"
                >
                    <div v-if="isComponentPickerOpen">
                        <div class="flex items-center justify-between border-b border-gray-100 pb-2 px-3">
                            <span class="text-xs font-semibold text-gray-700">{{ _mft('page:aiPageBuilder.attach.title') }}</span>
                            <button
                                type="button"
                                class="text-gray-400 hover:text-gray-600"
                                @click="isComponentPickerOpen = false"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>
                        <div class="max-h-80 overflow-y-auto space-y-1 px-3 pb-6">
                            <p class="my-1 text-xs text-gray-500">{{ _mft('page:aiPageBuilder.attach.hint') }}</p>
                            <button
                                v-for="component in componentCatalog"
                                :key="component.type"
                                type="button"
                                @click="attachComponent(component)"
                                class="w-full text-left p-2 pt-1.5 rounded-lg border border-gray-200 hover:border-brandPrimary-300 hover:bg-brandPrimary-50 transition-colors"
                            >
                                <div class="text-sm font-medium text-gray-800">{{ _mft(component.nameKey) }}</div>
                                <div class="text-xs text-gray-500">{{ _mft(component.descriptionKey) }}</div>
                            </button>
                        </div>
                        <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white to-transparent h-6 pointer-events-none"></div>
                    </div>
                </div>
            </div>

            <!-- Pending attachment chips -->
            <div v-if="pendingAttachments.length" class="mb-2 flex flex-wrap gap-1.5">
                <span
                    v-for="(attachment, index) in pendingAttachments"
                    :key="index"
                    class="inline-flex items-center gap-1.5 rounded-full bg-aiAgentWebDesigner/30 py-1 pl-2.5 pr-1.5 text-xs font-medium text-gray-700"
                >
                    <button
                        type="button"
                        class="hover:text-gray-900"
                        :title="_mft('page:aiPageBuilder.components.settings')"
                        @click="editAttachment(attachment, index)"
                    >
                        {{ attachmentLabel(attachment) }}
                    </button>
                    <button
                        type="button"
                        class="flex h-4 w-4 items-center justify-center rounded-full text-gray-500 hover:bg-white/60 hover:text-gray-700"
                        :title="_mft('shared:action.delete')"
                        @click="removeAttachment(index)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </span>
            </div>

            <div class="flex items-end space-x-2">
                <div class="flex-1 relative w-full">
                    <textarea
                        v-model="messageInput"
                        @keydown="handleKeydown"
                        :disabled="isGenerating"
                        :placeholder="_mft('page:aiPageBuilder.placeholder')"
                        rows="3"
                        maxlength="10000"
                        class="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-aiAgentWebDesigner focus:border-aiAgentWebDesigner disabled:opacity-50"
                    ></textarea>
                    <span class="absolute right-2 bottom-2 pointer-events-none text-xxs opacity-50">
                        {{ 10000 - messageInput.length }}
                    </span>
                </div>
                <div class="flex flex-col">
                    <button-base
                        @click="toggleComponentPicker"
                        :style-type="isComponentPickerOpen ? 'tinted' : 'secondary'"
                        :disabled="isGenerating"
                        size="sm"
                        class="flex-shrink-0 !px-2 !py-2 mb-1 -translate-y-px"
                        :title="_mft('page:aiPageBuilder.attach.button')"
                    >
                        <template #icon>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-myflowPurple" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                            </svg>
                        </template>
                    </button-base>
                    <button-base
                        @click="sendMessage"
                        style-type="primary"
                        :disabled="!canSend"
                        size="sm"
                        class="flex-shrink-0 !px-2 !py-2 mb-1 -translate-y-px"
                    >
                        <template #icon>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                            </svg>
                        </template>
                    </button-base>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% { transform: scale(0) rotate(-20deg); }
    60% { transform: scale(1.15) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
